import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/components/theme-v2/ThemeContext';
import {
  StyledAreaBox,
  StyledColoredBox,
  CardHeader,
  CardTitle,
  PopularRibbon,
  ActiveChip,
  ExpiredChip,
  StyledList,
  StyledListItem,
  CardBody,
  PriceRow,
  PriceText,
  CancelButton,
  StyledChip,
  EnterpriseText,
  PrimaryButton,
  ActiveButton,
} from '@pages/Pricing/styled';
import styled, { keyframes } from 'styled-components';

import { useScreenSizeContext } from '@/shared/context/ScreenSizeContext';

// Animation
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Horizontal card wrapper - extends the vertical card for Settings page
const HorizontalCardWrapper = styled.div<{ $active?: boolean; $popular?: boolean; $isDark?: boolean; $isMobile?: boolean }>`
  width: 100%;
  background: ${props => props.$isDark
    ? 'linear-gradient(180deg, #151c24 0%, #121920 100%)'
    : 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)'};
  border: 1px solid ${props => props.$active
    ? 'rgba(0, 255, 127, 0.5)'
    : props.$popular
      ? 'rgba(0, 200, 224, 0.4)'
      : props.$isDark ? 'rgba(42, 53, 68, 0.6)' : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeInUp} 0.5s ease-out backwards;
  display: flex;
  flex-direction: ${props => props.$isMobile ? 'column' : 'row'};

  ${props => props.$active && `
    box-shadow:
      0 0 0 1px rgba(0, 255, 127, 0.2),
      0 10px 30px -15px rgba(0, 255, 127, 0.3);
  `}

  ${props => props.$popular && !props.$active && `
    box-shadow:
      0 0 0 1px rgba(0, 200, 224, 0.1),
      0 10px 30px -15px rgba(0, 200, 224, 0.25);
  `}

  ${props => !props.$popular && !props.$active && `
    box-shadow: ${props.$isDark
      ? '0 4px 15px rgba(0, 0, 0, 0.2)'
      : '0 2px 10px rgba(0, 0, 0, 0.04)'};
  `}

  &:hover {
    transform: translateY(-3px);
    border-color: ${props => props.$active ? 'rgba(0, 255, 127, 0.6)' : 'rgba(0, 200, 224, 0.5)'};
    box-shadow: ${props => props.$active
      ? '0 15px 40px -15px rgba(0, 255, 127, 0.35)'
      : '0 15px 40px -15px rgba(0, 200, 224, 0.25)'};
  }
`;

const InfoSection = styled.div<{ $bgColor?: string; $isDark?: boolean; $isPopular?: boolean; $isMobile?: boolean }>`
  flex: 1;
  padding: 24px;
  min-width: ${props => props.$isMobile ? 'auto' : '280px'};
  background: ${props => {
    if (props.$isPopular) {
      return props.$isDark
        ? 'linear-gradient(135deg, rgba(0, 200, 224, 0.1) 0%, rgba(0, 200, 224, 0.03) 100%)'
        : 'linear-gradient(135deg, rgba(0, 200, 224, 0.06) 0%, rgba(0, 200, 224, 0.01) 100%)';
    }
    return props.$isDark
      ? 'rgba(0, 200, 224, 0.03)'
      : 'rgba(0, 0, 0, 0.01)';
  }};
  border-right: ${props => props.$isMobile ? 'none' : `1px solid ${props.$isDark ? 'rgba(42, 53, 68, 0.4)' : 'rgba(0, 0, 0, 0.06)'}`};
  border-bottom: ${props => props.$isMobile ? `1px solid ${props.$isDark ? 'rgba(42, 53, 68, 0.4)' : 'rgba(0, 0, 0, 0.06)'}` : 'none'};
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PriceSection = styled.div<{ $isDark?: boolean; $isMobile?: boolean }>`
  flex: 1;
  padding: 24px;
  min-width: ${props => props.$isMobile ? 'auto' : '260px'};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h3<{ $isDark?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.$isDark ? '#f0f4f8' : '#0f172a'};
  margin: 0;
  letter-spacing: -0.01em;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FeatureItem = styled.li<{ $isDark?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.875rem;
  color: ${props => props.$isDark ? '#94a3b8' : '#64748b'};
  line-height: 1.5;

  &::before {
    content: '';
    width: 16px;
    height: 16px;
    background: linear-gradient(135deg, rgba(0, 200, 224, 0.2) 0%, rgba(0, 200, 224, 0.1) 100%);
    border-radius: 50%;
    flex-shrink: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='10' height='8' viewBox='0 0 10 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 4L3.5 6.5L9 1' stroke='%2300c8e0' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
  }
`;

const Price = styled.div<{ $isDark?: boolean }>`
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.$isDark ? '#f0f4f8' : '#0f172a'};
  letter-spacing: -0.02em;
`;

const TokenChip = styled.div<{ $isDark?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: ${props => props.$isDark
    ? 'rgba(0, 200, 224, 0.08)'
    : 'rgba(0, 200, 224, 0.06)'};
  border: 1px solid ${props => props.$isDark
    ? 'rgba(0, 200, 224, 0.15)'
    : 'rgba(0, 200, 224, 0.2)'};
  border-radius: 8px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${props => props.$isDark ? '#00b8d0' : '#0099ad'};
  width: fit-content;
`;

const EnterpriseInfo = styled.p<{ $isDark?: boolean }>`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.875rem;
  color: ${props => props.$isDark ? '#8b9cb3' : '#64748b'};
  margin: 0;
  line-height: 1.5;
`;

const ActionButtonPrimary = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 12px 20px;
  background: ${props => props.$disabled
    ? 'rgba(100, 116, 139, 0.2)'
    : 'linear-gradient(135deg, #00c8e0 0%, #0099ad 100%)'};
  border: none;
  border-radius: 10px;
  color: ${props => props.$disabled ? '#64748b' : '#0a0e14'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.02em;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px -8px rgba(0, 200, 224, 0.5);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ActionButtonActive = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 12px 20px;
  background: linear-gradient(135deg, rgba(0, 200, 127, 0.15), rgba(0, 200, 127, 0.08));
  border: 1px solid rgba(0, 200, 127, 0.25);
  border-radius: 10px;
  color: #00c87f;
  cursor: default;
  letter-spacing: 0.02em;
`;

const CancelBtn = styled.button`
  padding: 6px;
  background: transparent;
  border: 1px solid rgba(255, 71, 87, 0.2);
  border-radius: 6px;
  color: #ff4757;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 71, 87, 0.1);
    border-color: rgba(255, 71, 87, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Icons
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const CancelIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="M15 9l-6 6M9 9l6 6" />
  </svg>
);

interface PlanCardProps {
  isActive: boolean;
  isExpired?: boolean;
  isPopular?: boolean;
  isFree?: boolean;
  isEnterprise?: boolean;
  bgColor: string;
  tokensInPackage?: number;
  title: string;
  subtitle: string;
  packagePriceTitle: string;
  onClick: () => void;
  onCancelClick: () => void;
  isLoading?: boolean;
  packageButtonLocale?: string;
}

const PlanCard: FC<PlanCardProps> = ({
  title,
  subtitle,
  isActive,
  bgColor,
  packagePriceTitle,
  onClick,
  isPopular = false,
  isFree = false,
  isEnterprise = false,
  isExpired = false,
  tokensInPackage = 0,
  isLoading,
  packageButtonLocale = '',
  onCancelClick,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isLargerScreen } = useScreenSizeContext();
  const isDarkMode = theme === 'dark';

  const tokensPerMonth = `${tokensInPackage.toLocaleString()} ${isFree ? t('billing.tokens') : t('billing.tokensPerMonth')}`;
  const features = t(subtitle)
    .trim()
    .split('; ')
    .map(desc => desc.replace(';', '').trim())
    .filter(Boolean);
  const freeConstraint = isActive && isFree;

  return (
    <HorizontalCardWrapper
      $active={isActive && !isExpired}
      $popular={isPopular && !isActive}
      $isDark={isDarkMode}
      $isMobile={!isLargerScreen}
    >
      <InfoSection
        $isDark={isDarkMode}
        $isPopular={isPopular && !isActive}
        $isMobile={!isLargerScreen}
      >
        <HeaderRow>
          <Title $isDark={isDarkMode}>{t(title)}</Title>
          {isExpired && <ExpiredChip>{t('billing.other.expiredChip')}</ExpiredChip>}
          {isActive && !isExpired && <ActiveChip>{t('billing.other.activeChip')}</ActiveChip>}
          {isPopular && !isActive && <PopularRibbon />}
        </HeaderRow>

        <FeaturesList>
          {features.map((feature, idx) => (
            <FeatureItem key={idx} $isDark={isDarkMode}>{feature}</FeatureItem>
          ))}
        </FeaturesList>
      </InfoSection>

      <PriceSection $isDark={isDarkMode} $isMobile={!isLargerScreen}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Price $isDark={isDarkMode}>{t(packagePriceTitle)}</Price>
            {isActive && !isFree && (
              <CancelBtn
                onClick={onCancelClick}
                disabled={isLoading}
                title={t('billing.other.cancelPlan')}
              >
                <CancelIcon />
              </CancelBtn>
            )}
          </div>

          {(isFree || tokensInPackage > 0) && (
            <TokenChip $isDark={isDarkMode}>{tokensPerMonth}</TokenChip>
          )}

          {isEnterprise && (
            <EnterpriseInfo $isDark={isDarkMode}>{t('billing.other.enterpriseText')}</EnterpriseInfo>
          )}
        </div>

        {freeConstraint ? (
          <ActionButtonActive>
            <CheckIcon />
            {t('billing.other.activeChip')}
          </ActionButtonActive>
        ) : !isFree ? (
          <ActionButtonPrimary
            onClick={onClick}
            disabled={isLoading}
            $disabled={isLoading}
          >
            {isActive ? t('billing.other.topUpText') : t(packageButtonLocale)}
          </ActionButtonPrimary>
        ) : null}
      </PriceSection>
    </HorizontalCardWrapper>
  );
};

export default memo(PlanCard);
